import { extractTodoIdFromBasecampUrl, extractTodoIdFromPath } from '@/lib/todo-id-from-path';

/** Fired when async Basecamp todo ingestion finishes (chat polling detects success). */
export const BASECAMP_INGESTION_COMPLETE = 'basecamp-ingestion-complete';

/** Fired to refresh open Basecamp todo chrome (work specs, comments, etc.). */
export const BASECAMP_TODO_REFRESH = 'basecamp-todo-refresh';

export interface BasecampIngestionCompleteDetail {
  todoId?: string;
  filePath?: string;
  conversationId?: string;
}

export interface BasecampTodoRefreshDetail {
  todoId?: string;
  projectId?: string;
}

/** Dispatches basecamp-todo-refresh so open todo chrome reloads work specs and related data. */
export function dispatchBasecampTodoRefresh(detail: BasecampTodoRefreshDetail = {}): void {
  window.dispatchEvent(new CustomEvent(BASECAMP_TODO_REFRESH, { detail }));
}

/** Resolve a todo id from a notes write (tool result or draft push). */
export function todoIdFromNotesWrite(detail: {
  todoId?: string | number | null
  todoUrl?: string | null
} = {}): string | undefined {
  if (detail.todoId != null && String(detail.todoId).trim()) {
    return String(detail.todoId)
  }
  return extractTodoIdFromBasecampUrl(detail.todoUrl) ?? undefined
}

/** After chat/draft writes notes, reload the open todo view (description + YAML). */
export function dispatchBasecampTodoRefreshFromNotesWrite(detail: {
  todoId?: string | number | null
  todoUrl?: string | null
  projectId?: string | null
} = {}): void {
  const todoId = todoIdFromNotesWrite(detail)
  dispatchBasecampTodoRefresh({
    ...(todoId ? { todoId } : {}),
    ...(detail.projectId ? { projectId: String(detail.projectId) } : {}),
  })
}

/** Dispatch refresh when a document path is under a todo folder. */
export function dispatchBasecampTodoRefreshForDocumentPath(
  filePath: string | null | undefined,
): void {
  const todoId = extractTodoIdFromPath(filePath ?? undefined);
  if (todoId) dispatchBasecampTodoRefresh({ todoId });
}

export function dispatchBasecampIngestionComplete(
  detail: BasecampIngestionCompleteDetail,
): void {
  window.dispatchEvent(
    new CustomEvent(BASECAMP_INGESTION_COMPLETE, { detail }),
  );
}

/** Returns detail when a basecamp-ingestion conversation has finished successfully. */
export function getIngestionCompleteDetail(
  conversation: {
    id?: string;
    status?: string;
    metadata?: Record<string, unknown>;
    messages?: Array<{
      content?: string;
      metadata?: Record<string, unknown>;
    }>;
  },
): BasecampIngestionCompleteDetail | null {
  const meta = conversation.metadata ?? {};
  const isIngestion =
    meta.type === 'basecamp-ingestion' || meta.type === 'ingestion';
  if (!isIngestion) return null;

  const messages = conversation.messages ?? [];
  const isComplete =
    meta.ingestion_complete === true ||
    conversation.status === 'completed' ||
    messages.some(
      (m) =>
        m.metadata?.type === 'ingestion-success' ||
        /\*\*Todo ingested successfully!\*\*/i.test(m.content ?? '') ||
        (m.content?.includes('\u2705') &&
          (m.content.includes('successfully') || m.content.includes('complete'))),
    );
  if (!isComplete) return null;

  const successMsg = messages.find(
    (m) => m.metadata?.type === 'ingestion-success',
  );
  const filePath =
    (successMsg?.metadata?.filePath as string | undefined) ||
    (meta.todoFile as string | undefined) ||
    (meta.messageFile as string | undefined);

  return {
    todoId: meta.todoId != null ? String(meta.todoId) : undefined,
    filePath,
    conversationId: conversation.id,
  };
}

/** Dispatch once per conversationId when ingestion completes. */
export function maybeDispatchIngestionComplete(
  conversation: Parameters<typeof getIngestionCompleteDetail>[0],
  conversationId: string,
  dispatched: Set<string>,
): boolean {
  if (dispatched.has(conversationId)) return false;
  const detail = getIngestionCompleteDetail(conversation);
  if (!detail) return false;
  dispatched.add(conversationId);
  dispatchBasecampIngestionComplete(detail);
  return true;
}
